import React from 'react';
import { Link } from 'react-router-dom';
import useFadeIn from '../hooks/useFadeIn';
import { captureOutboundLinkClick, captureResourceOpen } from '../utils/analytics';
import { getNewsletterIssuesNewestFirst, NEWSLETTER_SUBSCRIBE_URL } from './newsletterIssues';

function captureIssueOpen(issue, placement) {
  captureResourceOpen(issue.id, {
    resource_title: issue.title,
    resource_type: 'newsletter',
    source: 'newsletter-archive',
    placement,
  });
}

export default function NewsletterArchivePage() {
  const [heroRef, heroVisible] = useFadeIn(0.05);
  const [issuesRef, issuesVisible] = useFadeIn(0.08);
  const issues = getNewsletterIssuesNewestFirst();

  return (
    <main className="bg-white">
      <header className="bg-[#f5f5f7] px-8 pb-8 pt-14 md:px-16">
        <div
          ref={heroRef}
          className={`fade-in-section ${heroVisible ? 'is-visible' : ''} mx-auto max-w-[860px]`}
        >
          <p className="mb-5 text-[13px] font-medium uppercase tracking-widest text-[#86868b]">
            Newsletter
          </p>
          <h1 className="mb-4 text-4xl font-semibold leading-tight tracking-tight text-[#1d1d1f] md:text-5xl">
            MedHug Newsletter
          </h1>
          <p className="max-w-xl text-lg font-light leading-relaxed text-[#86868b]">
            Practical Medicare coverage and medication-access guidance, published every month for healthcare professionals.
          </p>
        </div>
      </header>

      <section
        ref={issuesRef}
        aria-labelledby="all-issues-heading"
        className={`fade-in-section ${issuesVisible ? 'is-visible' : ''} mx-auto max-w-[860px] px-8 pb-10 pt-6 md:px-16`}
      >
        <h2 id="all-issues-heading" className="py-5 text-[13px] font-medium uppercase tracking-widest text-[#86868b]">
          All issues
        </h2>
        <div className="divide-y divide-[#e5e5ea] border-t border-[#e5e5ea]">
          {issues.map((issue) => (
            <article key={issue.id} className="group py-7 md:py-8">
              <div className="mb-3 flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                  Newsletter
                </span>
                <span className="text-xs text-[#86868b]">
                  <time dateTime={issue.isoDate}>{issue.dateLabel}</time>
                </span>
                <span className="text-xs text-[#86868b]">·</span>
                <span className="text-xs text-[#86868b]">{issue.volume} · {issue.readTime}</span>
              </div>
              <Link to={issue.href} onClick={() => captureIssueOpen(issue, 'archive-title')}>
                <h3 className="mb-2 text-xl font-semibold leading-snug text-[#1d1d1f] md:text-[22px]">
                  {issue.title}
                </h3>
              </Link>
              <p className="mb-4 max-w-[720px] text-[15px] leading-relaxed text-[#86868b]">{issue.summary}</p>
              <Link
                to={issue.href}
                aria-label={`Read ${issue.dateLabel} issue`}
                className="inline-flex items-center gap-1 text-[14px] font-medium text-[#06c] transition-colors group-hover:text-[#004aad]"
                onClick={() => captureIssueOpen(issue, 'archive-cta')}
              >
                Read issue
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7M17 7H7M17 7v10" />
                </svg>
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#1d1d1f] px-8 py-10 md:px-16">
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-blue-600/15 via-purple-600/10 to-transparent blur-3xl" />
        <div className="relative mx-auto flex max-w-[860px] flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div>
            <h2 className="mb-2 text-xl font-semibold text-white">Get the next issue in your inbox.</h2>
            <p className="text-[15px] font-light text-white/50">
              Monthly coverage updates and clinic-tested medication-access workflows from MedHug.
            </p>
          </div>
          <a
            href={NEWSLETTER_SUBSCRIBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center rounded-full bg-white px-7 py-3.5 text-[15px] font-medium text-[#1d1d1f] transition-colors hover:bg-gray-100"
            onClick={() => captureOutboundLinkClick('newsletter-archive-subscribe', NEWSLETTER_SUBSCRIBE_URL, { placement: 'newsletter-archive' })}
          >
            Subscribe to MedHug Newsletter
          </a>
        </div>
      </section>
    </main>
  );
}
